import type { DistortionPoint } from "shared/schema/src/types";

/**
 * Apply mesh-based distortion to image data
 * @param imageData - ImageData to modify
 * @param distortionPoints - Array of distortion control points
 * @param gridSize - Size of the mesh grid (default: 20)
 * @param power - Falloff power for distortion effect (default: 1.0)
 * @param range - Range of distortion effect in pixels (default: 100)
 * @returns New distorted ImageData
 */
export function applyMeshDistortionToImageData(
	imageData: ImageData,
	distortionPoints: Array<{
		original: DistortionPoint;
		current: DistortionPoint;
	}>,
	gridSize = 20,
	power = 1.0,
	range = 100,
): ImageData {
	if (distortionPoints.length === 0) return imageData;

	const data = imageData.data;
	const width = imageData.width;
	const height = imageData.height;

	// Create a copy of the original data to read from
	const originalData = new Uint8ClampedArray(data);

	// Create mesh grid (similar to PixiJS approach)
	const cols = gridSize;
	const rows = gridSize;
	const totalVertices = (cols + 1) * (rows + 1);

	// Initialize mesh vertices
	const meshVertices = new Array(totalVertices);
	for (let i = 0; i <= rows; i++) {
		for (let j = 0; j <= cols; j++) {
			const vertexIndex = i * (cols + 1) + j;
			const x = (j / cols) * width;
			const y = (i / rows) * height;
			meshVertices[vertexIndex] = { x, y };
		}
	}

	// Apply distortion points to mesh vertices
	for (const point of distortionPoints) {
		const centerX = point.original.x;
		const centerY = point.original.y;
		const displacementX = point.current.x - point.original.x;
		const displacementY = point.current.y - point.original.y;

		// Apply displacement to nearby vertices with falloff
		for (const vertex of meshVertices) {
			const distance = Math.sqrt(
				(vertex.x - centerX) ** 2 + (vertex.y - centerY) ** 2,
			);

			if (distance < range) {
				const falloff = (1 - distance / range) ** power; // Power-based falloff

				// Normal distortion (drag-based)
				// Invert displacement so distortion follows mouse direction
				vertex.x -= displacementX * falloff;
				vertex.y -= displacementY * falloff;
			}
		}
	}

	// Create output image data
	const output = new ImageData(width, height);
	const outPixels = output.data;

	// For each pixel in the output, sample from the deformed mesh
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			// Find which grid cell this pixel belongs to
			const gridX = Math.floor((x / width) * cols);
			const gridY = Math.floor((y / height) * rows);

			// Clamp to valid range
			const clampedGridX = Math.max(0, Math.min(cols - 1, gridX));
			const clampedGridY = Math.max(0, Math.min(rows - 1, gridY));

			// Get the four corner vertices of this grid cell
			const topLeft = meshVertices[clampedGridY * (cols + 1) + clampedGridX];
			const topRight =
				meshVertices[clampedGridY * (cols + 1) + clampedGridX + 1];
			const bottomLeft =
				meshVertices[(clampedGridY + 1) * (cols + 1) + clampedGridX];
			const bottomRight =
				meshVertices[(clampedGridY + 1) * (cols + 1) + clampedGridX + 1];

			// Calculate local coordinates within the grid cell
			const localX = (x / width) * cols - clampedGridX;
			const localY = (y / height) * rows - clampedGridY;

			// Bilinear interpolation to find source position
			const sourceX =
				topLeft.x * (1 - localX) * (1 - localY) +
				topRight.x * localX * (1 - localY) +
				bottomLeft.x * (1 - localX) * localY +
				bottomRight.x * localX * localY;

			const sourceY =
				topLeft.y * (1 - localX) * (1 - localY) +
				topRight.y * localX * (1 - localY) +
				bottomLeft.y * (1 - localX) * localY +
				bottomRight.y * localX * localY;

			// Clamp source coordinates
			const clampedSourceX = Math.max(
				0,
				Math.min(width - 1, Math.round(sourceX)),
			);
			const clampedSourceY = Math.max(
				0,
				Math.min(height - 1, Math.round(sourceY)),
			);

			// Sample pixel from source position
			const sourceIndex = (clampedSourceY * width + clampedSourceX) * 4;
			const destIndex = (y * width + x) * 4;

			outPixels[destIndex] = originalData[sourceIndex] || 0; // R
			outPixels[destIndex + 1] = originalData[sourceIndex + 1] || 0; // G
			outPixels[destIndex + 2] = originalData[sourceIndex + 2] || 0; // B
			outPixels[destIndex + 3] = originalData[sourceIndex + 3] || 255; // A
		}
	}

	return output;
}
